import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Download as DownloadIcon, PictureAsPdf, Refresh as RefreshIcon, Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import { formatCurrencyWithSymbol } from '../../utils/currencyUtils';
import { triggerSignedUrlDownload } from '../../utils/download';

const QuotePdfList = () => {
  const { t, i18n } = useTranslation();
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchPdfs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(limit),
        ...(appliedSearch ? { search: appliedSearch } : {})
      });
      const response = await makeApiCall(`api/quotes/pdfs?${params.toString()}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('reports.quotePdfs.loadError'));
      }

      setPdfs(data.pdfs || []);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      setError(err.message || t('reports.quotePdfs.loadError'));
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, limit, page, t]);

  useEffect(() => {
    fetchPdfs();
  }, [fetchPdfs]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(0);
    setAppliedSearch(search.trim());
  };

  const handleDownload = async (pdf, version = 'original') => {
    setDownloadingId(`${pdf.id}-${version}`);
    setError('');
    try {
      const response = await makeApiCall(`api/quotes/pdfs/${pdf.id}/download?version=${version}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('reports.quotePdfs.downloadError'));
      }

      // TKT-233: signed URL has attachment disposition — downloads in
      // place, no new tab.
      triggerSignedUrlDownload(data.signed_url);
    } catch (err) {
      setError(err.message || t('reports.quotePdfs.downloadError'));
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Intl.DateTimeFormat(i18n.language || undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(new Date(value));
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <PictureAsPdf sx={{ fontSize: 36, color: '#d32f2f' }} />
        <Typography variant="h4" sx={{ color: '#000062', fontWeight: 'bold', fontSize: { xs: '1.6rem', md: '2.125rem' } }}>
          {t('reports.quotePdfs.title')}
        </Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('reports.quotePdfs.description')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper
        component="form"
        onSubmit={handleSearchSubmit}
        sx={{ p: { xs: 2, md: 3 }, mb: 3 }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            fullWidth
            label={t('reports.quotePdfs.searchLabel')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('reports.quotePdfs.searchPlaceholder')}
            inputProps={{ 'aria-label': t('reports.quotePdfs.searchAriaLabel') }}
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={<SearchIcon />}
            aria-label={t('reports.quotePdfs.searchAriaLabel')}
            sx={{ bgcolor: '#1976d2', minWidth: { sm: 120 } }}
          >
            {t('common.search')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchPdfs}
            aria-label={t('reports.quotePdfs.refreshAriaLabel')}
            sx={{ minWidth: { sm: 120 } }}
          >
            {t('common.refresh')}
          </Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table stickyHeader aria-label={t('reports.quotePdfs.tableAriaLabel')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('reports.quotePdfs.generatedOn')}</TableCell>
              <TableCell>{t('quotes.quoteNumber')}</TableCell>
              <TableCell>{t('reports.customer')}</TableCell>
              <TableCell>{t('reports.quotePdfs.filename')}</TableCell>
              <TableCell>{t('reports.status')}</TableCell>
              <TableCell align="right">{t('quotes.totalAmount')}</TableCell>
              <TableCell align="right">{t('reports.quotePdfs.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress aria-label={t('common.loading')} />
                </TableCell>
              </TableRow>
            ) : pdfs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" gutterBottom>
                    {t('reports.quotePdfs.noPdfs')}
                  </Typography>
                  {/* TKT-211: legacy quotes were imported as records but
                      their PDF files were not migrated to S3. Explain
                      that this view only shows PDFs that have actually
                      been generated, so an empty search isn't a bug -
                      the operator can open the quote and Generate PDF
                      to populate it. */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 560, mx: 'auto' }}>
                    {t(
                      'reports.quotePdfs.noPdfsHint',
                      'This list only includes quote PDFs that have been generated and saved. Legacy quotes imported from the previous system do not have PDFs until they are re-generated from the Quotes screen.'
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pdfs.map((pdf) => (
                <TableRow key={pdf.id} hover>
                  <TableCell>{formatDate(pdf.created_at)}</TableCell>
                  <TableCell>{pdf.quote_number}</TableCell>
                  <TableCell>{pdf.customer_name || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {pdf.filename || '-'}
                    </Typography>
                    {pdf.signed_filename && (
                      <Typography variant="caption" color="text.secondary">
                        {t('reports.quotePdfs.signedFilename')}: {pdf.signed_filename}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={pdf.quote_status || '-'} />
                      {pdf.emailed_successfully && (
                        <Chip size="small" color="success" variant="outlined" label={t('reports.quotePdfs.emailed')} />
                      )}
                      {pdf.envelope_status && (
                        <Chip size="small" color="primary" variant="outlined" label={pdf.envelope_status} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{formatCurrencyWithSymbol(pdf.total_amount || 0)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('reports.quotePdfs.downloadOriginal')}>
                      <span>
                        <IconButton
                          onClick={() => handleDownload(pdf)}
                          disabled={downloadingId !== null}
                          aria-label={t('reports.quotePdfs.downloadOriginalAriaLabel', { quoteNumber: pdf.quote_number })}
                        >
                          {downloadingId === `${pdf.id}-original` ? <CircularProgress size={20} /> : <DownloadIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {pdf.has_signed_pdf && (
                      <Tooltip title={t('reports.quotePdfs.downloadSigned')}>
                        <span>
                          <IconButton
                            onClick={() => handleDownload(pdf, 'signed')}
                            disabled={downloadingId !== null}
                            aria-label={t('reports.quotePdfs.downloadSignedAriaLabel', { quoteNumber: pdf.quote_number })}
                          >
                            {downloadingId === `${pdf.id}-signed` ? <CircularProgress size={20} /> : <PictureAsPdf />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={limit}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setLimit(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
};

export default QuotePdfList;
